/**
 * Module-level singleton state for the Net Impact drill-in side panel
 * (PLT-4915).
 *
 * Same pub-sub pattern as `chat-panel-state.ts` — the panel is opened
 * from places that may live deep inside the component tree (e.g. an SVG
 * `<g>` inside an `ImpactBar`), and we want those call sites to be able
 * to call a plain function rather than first wiring a hook through
 * intermediate components.
 *
 * The panel itself subscribes via `useSyncExternalStore` and renders
 * nothing when closed, so it is safe to mount unconditionally at the
 * app root.
 *
 * Lifecycle:
 *   - `openNetImpactDrillIn(payload)` — replace the current payload and
 *     show the panel. Always re-mounts (`nonce++`) so the new payload
 *     lands in a fresh component tree, eliminating any chance of stale
 *     state from a previous open.
 *   - `closeNetImpactDrillIn()` — hide the panel. We do NOT bump the
 *     nonce on close so a re-open without a new payload (currently not
 *     exercised) reads the same content; but a re-open *with* a new
 *     payload always re-mounts via the open path.
 *
 * The "hand-off to chat" path uses `closeNetImpactDrillIn()` immediately
 * before calling `openChatPanel({...})` from the chat tab — the two
 * panels render in the same right-edge slot, so the drill-in must
 * unmount before the chat panel mounts to avoid stacking.
 */
import { uiImpactCategoryByCode } from '@upright/common/model/input/impacts';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { ModelEntityType } from '@upright/common/model/input/types';
import { friendlyDimensionName } from '@upright/common/model/texts/friendly-dimension-name';
import { Dimension, ORDERED_IMPACTS_BY_DIMENSION } from '@upright/common/model/types';
import { friendlyShortImpactTitle } from '@upright/common/utils';

/** Tab the panel should show when it mounts.
 *
 * Contributing-products is split per valence (PLT-4915 Janne review v3) so a
 * click on the negative bar lands directly on the negative contributors list
 * without an extra tab change. `units` is the everyday-equivalents tab
 * (renamed from "real-world units" in the same review). `about` is the
 * "What's measured" tab (PLT-5714) — the category label click lands here so
 * the whole row opens the same panel, only the starting tab differing. */
export type NetImpactDrillInTab = 'about' | 'positive' | 'negative' | 'units' | 'chat';

/**
 * What the panel is drilling into (PLT-4940). The panel was originally
 * keyed on a single `impactCode` (PLT-4915); it now also accepts a
 * dimension (Environment / Society / Health / Knowledge), which is an
 * aggregate of ~5 impact categories. A discriminated union keeps every
 * consumer honest about which grain it is rendering — the dimension
 * grain has no single `impactCode`, so the per-category "everyday
 * units" tab is hidden at dimension grain (see `panel.tsx`) and the
 * contributors / chat tabs aggregate across the dimension's member
 * categories (see `drillInImpactCodes`).
 */
export type NetImpactDrillInTarget =
  | { grain: 'impact'; impactCode: ImpactCategoryId }
  | { grain: 'dimension'; dimension: Dimension };

/**
 * Payload threaded into the panel by the click handler. Everything the
 * panel needs to render its tabs without depending on the
 * `CarouselContext` (the panel is mounted at the app root, outside any
 * lens context).
 */
export interface NetImpactDrillInPayload {
  /** Impact-category or dimension the panel is drilling into. */
  target: NetImpactDrillInTarget;
  /**
   * Cents-per-dollar score per valence. Either may be null when the
   * company has no impact data for that valence (rare but possible for
   * smaller / data-light companies).
   */
  positiveCentsPerDollar: number | null;
  negativeCentsPerDollar: number | null;
  /**
   * Dollars-per-year score per valence. Needed for the *Real-world
   * units* tab — `dollarsToPhysicalComparisons(dollars, impact, valence)`
   * is the pure conversion that produces the DALY / GHG-tons / etc.
   * equivalents shown in the second-screenshot reference design.
   */
  positiveDollarsPerYear: number | null;
  negativeDollarsPerYear: number | null;
  /**
   * The entity rendered on the page the click originated from. Used by
   * the panel header (company name in subtitle), by the chat tab's
   * handoff to `openChatPanel` (entity binding), and by the
   * Contributing-products tab's "View full drilldown →" link.
   */
  entity: {
    id: string;
    type: ModelEntityType;
    name: string | undefined;
  };
  /**
   * Model result id — required to load product-level contributions
   * (`useCompanyDrilldownContributions`) and to build the drilldown URL.
   * May be undefined for entities without a backing result; the
   * Contributing-products tab falls back to a "no data" state in that
   * case.
   */
  modelResultId: string | undefined;
  /**
   * `{ id, name }` pairs for every product in the company's set, sourced
   * from `context.data.productNames` at click time. Lets the panel render
   * product names without re-fetching the data the carousel already had
   * loaded. Undefined when the carousel didn't have a product-names map
   * (e.g. portfolio entities — those don't open the drill-in today).
   */
  productNames: Array<{ id: string; name: string }> | undefined;
  /**
   * Whether the current user has product-view access — controls whether
   * the Contributing-products tab links each product to its product page.
   */
  hasProductViewAccess: boolean;
  /**
   * Page context label + URL forwarded to `openChatPanel` when the chat
   * tab hands off — the chip is the same visible "started from <page>"
   * chip the chat panel uses for the bar-click flow today.
   */
  pageContext: { label: string; href: string; entityType?: string; lensLabel?: string };
  /**
   * Whether value-chain mode is on at click time — captured from the
   * originating chart's `NetImpactLensSelectionState.showValueChains` ANDed
   * with the `value-chain-splits` release gate, exactly as `impact-bars.tsx`
   * computes it. The panel is mounted at the app root (outside the lens
   * context), so it can't read the selection state itself; threading the flag
   * through the payload keeps the drilldown tables' value-chain splits in lock
   * step with the chart's value-chain toggle. (PLT-5382)
   */
  showValueChains: boolean;
}

interface NetImpactDrillInState {
  open: boolean;
  payload: NetImpactDrillInPayload | null;
  /** Tab to render on mount. Defaults to `'contributing'`. */
  initialTab: NetImpactDrillInTab;
  /**
   * Monotonically-increasing counter, bumped on every `openNetImpactDrillIn`
   * call. The panel uses this as its `key` so each open forces a clean
   * unmount/mount cycle of the inner body — mirrors `chat-panel-state.ts`'s
   * `seedNonce` and avoids stale-state bugs from a re-open with different
   * payload.
   */
  nonce: number;
}

let _state: NetImpactDrillInState = { open: false, payload: null, initialTab: 'positive', nonce: 0 };
const _listeners = new Set<() => void>();

function notify(): void {
  for (const l of _listeners) l();
}

export function getNetImpactDrillInState(): NetImpactDrillInState {
  return _state;
}

export function subscribeNetImpactDrillInState(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

export interface OpenNetImpactDrillInOptions {
  payload: NetImpactDrillInPayload;
  /** Tab to land on. Defaults to `'positive'`. */
  initialTab?: NetImpactDrillInTab;
}

/**
 * Open the drill-in panel with `payload`. Always bumps `nonce` so the
 * panel re-mounts — there is no "continuation" mode for this surface,
 * unlike the chat panel: the drill-in is fundamentally a static view of
 * a single (entity, impact) pair, and reopening on a different bar
 * should look like a fresh open.
 */
export function openNetImpactDrillIn(options: OpenNetImpactDrillInOptions): void {
  _state = {
    open: true,
    payload: options.payload,
    initialTab: options.initialTab ?? 'positive',
    nonce: _state.nonce + 1,
  };
  notify();
}

export function closeNetImpactDrillIn(): void {
  if (!_state.open) return;
  // Preserve `payload` / `initialTab` / `nonce` on close — the panel
  // unmounts based on `open` alone, and not resetting the payload here
  // means a follow-up call site could theoretically re-open without a
  // new payload (we don't exercise that today, but it's the same
  // shape `chat-panel-state` uses for parity).
  _state = { ..._state, open: false };
  notify();
}

/* ─── Target helpers (PLT-4940) ─────────────────────────────────────────
 * Small pure derivations so every tab reads the (impact | dimension)
 * grain through one code path rather than branching on
 * `target.grain` in each consumer. */

/** True when the panel is drilling into a whole dimension rather than a
 *  single impact category — used to hide the per-category "Everyday
 *  units" tab and to drop the impact-scoped "view all" drilldown link. */
export function isDimensionTarget(target: NetImpactDrillInTarget): boolean {
  return target.grain === 'dimension';
}

/** The impact categories the panel covers: a single code at impact
 *  grain, or the dimension's ordered member categories at dimension
 *  grain. Consumers (contributors, chat chips) aggregate per product
 *  across this list. */
export function drillInImpactCodes(target: NetImpactDrillInTarget): ImpactCategoryId[] {
  return target.grain === 'impact' ? [target.impactCode] : ORDERED_IMPACTS_BY_DIMENSION[target.dimension];
}

/** Header title: friendly impact-category name (e.g. "GHG emissions")
 *  or dimension name (e.g. "Environment"). */
export function drillInTitle(target: NetImpactDrillInTarget): string {
  return target.grain === 'impact'
    ? friendlyShortImpactTitle(target.impactCode)
    : friendlyDimensionName(target.dimension);
}

/** Header definition line. Only impact categories carry a one-line
 *  `productViewExplanation`; dimensions have no single methodology
 *  one-liner, so we render no definition at dimension grain (the
 *  Definition element is conditionally rendered on a truthy value). */
export function drillInDefinition(target: NetImpactDrillInTarget): string | undefined {
  return target.grain === 'impact'
    ? uiImpactCategoryByCode[target.impactCode].explanations.productViewExplanation
    : undefined;
}

/** Stable seed key for the deterministic chat-chip picks — the impact
 *  code at impact grain, or `dim:<dimension>` at dimension grain. */
export function drillInSeedKey(target: NetImpactDrillInTarget): string {
  return target.grain === 'impact' ? target.impactCode : `dim:${target.dimension}`;
}
